import React, { useEffect, useRef, useState } from "react";
import { preferredLocationSelector } from "tsi-common-react/src/apps/common/selectors";
import { useAppDispatch } from "tsi-common-react/src/apps/reducers";
import { LoadingSpinner } from "tsi-common-react/src/common/LoadingSpinner";
import {
    RetailStoreEventCTAType,
    RetailStoreEventType,
} from "tsi-common-react/src/constants";
import {
    ILocation,
    IRetailStoreWithDistance,
} from "tsi-common-react/src/models/location";
import { ISyncStoreID } from "tsi-common-react/src/models/nominals";
import { trackRetailStoreEvent } from "tsi-common-react/src/utils/analytics";
import { t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import { useAppSelector } from "../../hooks";
import { FlagshipStoreInfoMap as FlagshipStoreInfoMapComponent } from "../components/FlagshipStoreInfoMap";
import { getFlagshipStoreByExternalID } from "../selectors";
import { loadFlagshipStorePage, loadStoreByExternalID } from "../thunks";

const loadStoreData = async (
    location: ILocation,
    storeID: ISyncStoreID,
    dispatch: ReturnType<typeof useAppDispatch>,
): Promise<void> => {
    await dispatch(loadStoreByExternalID({ location, storeID }));
    await dispatch(loadFlagshipStorePage(storeID));
};

const attachBookingButtonTracking = (
    store: IRetailStoreWithDistance | null,
): void => {
    // This may not be the optimal approach in React to locate the buttons used for booking appointments,
    // but it appears that these buttons are standardized and generated using the (Aligned) Rich Text block.
    const document = getDocument();
    if (!document) {
        return;
    }
    const buttons =
        document.querySelectorAll<HTMLAnchorElement>("a[class='button']");
    if (buttons) {
        buttons.forEach((element) => {
            const linkText = element.textContent?.trim();
            // TODO(i18n): Hardcoded string comparison needs i18n support or use data attribute for matching
            if (linkText && linkText.toLowerCase() === "book appointment") {
                const linkUrl = element.getAttribute("href");
                element.addEventListener("click", () => {
                    trackRetailStoreEvent(
                        RetailStoreEventType.TEMPUR_STORE,
                        store,
                        RetailStoreEventCTAType.APPOINTMENT,
                        linkText,
                        linkUrl,
                    );
                });
            }
        });
    }
};

// finds nearest flagship store to location
export const FlagshipStoreInfoMap = (props: {
    storeID: ISyncStoreID;
    mapMountElement: HTMLElement;
}): React.ReactNode => {
    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useAppDispatch();
    const previousLocationSnapshotRef = useRef<string | undefined>(undefined);
    const previousStoreIDRef = useRef<ISyncStoreID | null>(null);

    const location = useAppSelector(preferredLocationSelector);
    const store =
        useAppSelector((state) =>
            state.retail.stores.find((s) => s.external_id === props.storeID),
        ) || null;
    const storePage =
        useAppSelector((state) =>
            getFlagshipStoreByExternalID(
                state.retail.flagshipStorePages,
                props.storeID,
            ),
        ) || null;

    useEffect(() => {
        const locationSnapshot = JSON.stringify(location);
        const locationChanged =
            previousLocationSnapshotRef.current !== locationSnapshot;
        const storeIDChanged = previousStoreIDRef.current !== props.storeID;

        previousLocationSnapshotRef.current = locationSnapshot;
        previousStoreIDRef.current = props.storeID;

        if (!location || (!locationChanged && !storeIDChanged)) {
            return;
        }

        let isActive = true;

        const load = async () => {
            setIsLoading(true);
            await loadStoreData(location, props.storeID, dispatch);
            if (isActive) {
                setIsLoading(false);
            }
        };

        void load();

        return () => {
            isActive = false;
        };
    }, [location, props.storeID, dispatch]);

    useEffect(() => {
        if (!isLoading && store && storePage) {
            attachBookingButtonTracking(store);
        }
    }, [isLoading, store, storePage]);

    if (isLoading) {
        return <LoadingSpinner />;
    }

    // Stores and CMS store found
    if (!store || !storePage) {
        return (
            <span>{t`Sorry! We can't find a nearby Tempur-Pedic store.`}</span>
        );
    }

    return (
        <FlagshipStoreInfoMapComponent
            mapMountElement={props.mapMountElement}
            cmsProps={storePage}
            store={store}
        />
    );
};
